import React, { useState, useEffect, useRef } from "react";
import { auth, db, storage } from "../firebase-config";
import { onAuthStateChanged } from "firebase/auth";
import { collection, doc, getDoc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { ref as storageRef, uploadBytes } from "firebase/storage";
import PatientManager from './PatientManager';
import PatientNotes from './PatientNotes';
import PatientClinicalNotes from './PatientClinicalNotes';

function AudioUpload() {
    const [file, setFile] = useState(null);
    const [transcript, setTranscript] = useState("");
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [statusMessage, setStatusMessage] = useState("");
    const [quizData, setQuizData] = useState({ concepts: [], questions: [] });
    const [userAnswers, setUserAnswers] = useState({});
    const [feedback, setFeedback] = useState({});
    const [chatHistory, setChatHistory] = useState([]);
    const [userInput, setUserInput] = useState("");
    const [chatLoading, setChatLoading] = useState(false);
    const [clinicalNote, setClinicalNote] = useState("");
    const [patientId, setPatientId] = useState("");
    const [selectedPatientName, setSelectedPatientName] = useState("");
    const [processedNoteId, setProcessedNoteId] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [templateName, setTemplateName] = useState("");
    const [templateText, setTemplateText] = useState("");
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [transcriptionLimitError, setTranscriptionLimitError] = useState("");
    const [recording, setRecording] = useState(false);
    const mediaRecorderRef = useRef(null);
    const [recordedBlob, setRecordedBlob] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [currentUser, setCurrentUser] = useState(auth.currentUser);
    const [showPatientDrawer, setShowPatientDrawer] = useState(false);
    const [activeRecordingId, setActiveRecordingId] = useState("");
    const [chunkIndex, setChunkIndex] = useState(-1);
    const [recordingStartedAt, setRecordingStartedAt] = useState(null);
    const [pendingRecordings, setPendingRecordings] = useState([]);
    const [pendingLoading, setPendingLoading] = useState(false);
    const [selectedRecordingId, setSelectedRecordingId] = useState("");
    const [showPostRecordingPrompt, setShowPostRecordingPrompt] = useState(false);
    const [promptChunkCount, setPromptChunkCount] = useState(0);
    const chatEndRef = useRef(null);
    const activeRecordingIdRef = useRef("");
    const chunkIndexRef = useRef(-1);
    const recordingStartedAtRef = useRef(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
            if (user) loadUserTemplates(user);
            else { setTemplates([]); setSelectedTemplateId(null); }
        });
        if (auth.currentUser) loadUserTemplates(auth.currentUser);
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [chatHistory]);

    useEffect(() => {
        activeRecordingIdRef.current = activeRecordingId;
    }, [activeRecordingId]);

    useEffect(() => {
        chunkIndexRef.current = chunkIndex;
    }, [chunkIndex]);

    useEffect(() => {
        recordingStartedAtRef.current = recordingStartedAt;
    }, [recordingStartedAt]);

    useEffect(() => {
        if (patientId) setShowPatientDrawer(false);
        setActiveRecordingId("");
        setChunkIndex(-1);
        setRecordingStartedAt(null);
        setSelectedRecordingId("");
    }, [patientId]);

    useEffect(() => {
        if (!patientId) {
            setPendingRecordings([]);
            return;
        }
        setPendingLoading(true);
        const ref = collection(db, "patients", patientId, "recordings");
        const unsub = onSnapshot(ref, (snap) => {
            const items = snap.docs
                .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
                .filter((rec) => rec.status === "pending_transcription")
                .sort((a, b) => toMillis(b.recordingStartAt) - toMillis(a.recordingStartAt));
            setPendingRecordings(items);
            setPendingLoading(false);
        }, (err) => {
            console.error("Failed to load pending recordings", err);
            setPendingRecordings([]);
            setPendingLoading(false);
        });

        return () => unsub();
    }, [patientId]);

    useEffect(() => {
        const fetchPatient = async () => {
            if (!patientId) {
                setSelectedPatientName("");
                return;
            }
            try {
                const ref = doc(db, "patients", patientId);
                const snap = await getDoc(ref);
                if (!snap.exists()) {
                    setSelectedPatientName("Patient selected");
                    return;
                }
                const data = snap.data() || {};
                const name = `${data.firstName || ""} ${data.lastName || ""}`.trim();
                const dob = data.dob ? ` • ${data.dob}` : "";
                setSelectedPatientName(name ? `${name}${dob}` : "Patient selected");
            } catch (err) {
                console.error("Failed to load patient", err);
                setSelectedPatientName("Patient selected");
            }
        };
        fetchPatient();
    }, [patientId]);

    const loadUserTemplates = async (user) => {
        if (!user) return;
        try {
            const ref = doc(db, 'user_profiles', user.uid);
            const snap = await getDoc(ref);
            if (snap.exists()) {
                const data = snap.data() || {};
                setTemplates(Array.isArray(data.clinicalTemplates) ? data.clinicalTemplates : []);
                setSelectedTemplateId(data.selectedTemplateId || null);
            } else {
                setTemplates([]); setSelectedTemplateId(null);
            }
        } catch (err) { console.error('Error loading templates:', err); }
    };

    const persistTemplates = async (nextTemplates, nextSelectedId) => {
        if (!auth.currentUser) return;
        const ref = doc(db, 'user_profiles', auth.currentUser.uid);
        await setDoc(ref, {
            clinicalTemplates: nextTemplates,
            selectedTemplateId: nextSelectedId || null,
            updatedAt: serverTimestamp(),
        }, { merge: true });
    };

    const saveTemplate = async () => {
        if (!templateName.trim()) return alert('Please provide a template name');
        if (!templateText.trim()) return alert('Please enter template text');
        const entry = {
            id: Date.now().toString(),
            name: templateName.trim(),
            text: templateText,
            createdAt: Date.now(),
        };
        const next = [entry, ...templates];
        setTemplates(next);
        setTemplateName('');
        setTemplateText('');
        setSelectedTemplateId(entry.id);
        try { await persistTemplates(next, entry.id); }
        catch (err) { console.error('Error saving template:', err); alert('Failed to save template.'); }
    };

    const deleteTemplate = async (id) => {
        const next = templates.filter(t => t.id !== id);
        setTemplates(next);
        const nextSelectedId = selectedTemplateId === id ? null : selectedTemplateId;
        if (selectedTemplateId === id) setSelectedTemplateId(null);
        try { await persistTemplates(next, nextSelectedId); }
        catch (err) { console.error('Error deleting template:', err); }
    };

    const selectTemplate = async (id) => {
        setSelectedTemplateId(id);
        try { await persistTemplates(templates, id || null); }
        catch (err) { console.error('Error selecting template:', err); }
    };

    const blobToDataURL = (blob) => new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(blob);
    });

    const blobToBase64 = async (blob) => {
        const dataUrl = await blobToDataURL(blob);
        return dataUrl.split(',')[1];
    };

    const handleFileChange = (e) => {
        const f = e.target.files[0];
        setFile(f);
        setRecordedBlob(null);
        setAudioUrl(f ? URL.createObjectURL(f) : null);
    };

    const handleTranscribe = async () => {
        if (!file && !recordedBlob && !selectedRecordingId) return alert("Please record or select a recording first!");
        if (!patientId) return alert("Please select a patient first.");
        setTranscriptionLimitError("");
        setLoading(true);
        setUploadProgress(0);
        setStatusMessage("Preparing upload…");
        let isLimitError = false;
        try {
            const blob = file || recordedBlob;
            const base64Audio = blob ? await blobToBase64(blob) : null;
            const idToken = await auth.currentUser?.getIdToken();
            if (!idToken) throw new Error("User not authenticated");
            const processUrl = process.env.REACT_APP_PROCESS_AUDIO_URL;
            if (!processUrl) throw new Error("REACT_APP_PROCESS_AUDIO_URL is not set");
            const selectedTemplate = templates.find(t => t.id === selectedTemplateId) || null;

            setStatusMessage("Transcribing audio…");
            const resp = await fetch(processUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
                body: JSON.stringify({
                    audioData: base64Audio,
                    recordingId: selectedRecordingId || null,
                    patientId,
                    mimeType: blob?.type || "audio/webm",
                    fileName: file?.name || (selectedRecordingId ? `recording_${selectedRecordingId}.webm` : `recording_${Date.now()}.webm`),
                    templateId: selectedTemplate?.id || null,
                    templateName: selectedTemplate?.name || null,
                    templateText: selectedTemplate?.text || null,
                }),
            });

            const text = await resp.text();
            let data;
            try { data = JSON.parse(text); }
            catch (e) { throw new Error(`Non-JSON response: ${text}`); }
            if (!resp.ok) {
                if (resp.status === 429 && (data?.error || "").toLowerCase().includes("limit")) {
                    const limitMessage = "Error: Transcription limit reached. Please update subscription.";
                    setTranscriptionLimitError(limitMessage);
                    setStatusMessage(limitMessage);
                    isLimitError = true;
                }
                throw new Error(data.error || data.details || JSON.stringify(data));
            }

            const out = data.data || {};
            setTranscript(out.transcription || "");
            setClinicalNote(out.clinicalNotes || "");
            setProcessedNoteId(out.noteId || null);
            setStatusMessage("Done!");
            setQuizData({ concepts: [], questions: [] });
            setUserAnswers({});
            setFeedback({});
            setChatHistory([]);
            setSelectedRecordingId("");
        } catch (err) {
            console.error("Error transcribing:", err);
            if (!isLimitError) {
                setStatusMessage(`Error: ${err.message}`);
                alert(`Transcription failed: ${err.message}`);
            }
        } finally {
            setLoading(false);
            setUploadProgress(0);
        }
    };

    const startRecording = async () => {
        if (recording) return;
        setStatusMessage("Requesting microphone…");
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
            mediaRecorderRef.current = mediaRecorder;
            const chunks = [];
            mediaRecorder.ondataavailable = (e) => {
                if (e.data?.size > 0) {
                    chunks.push(e.data);
                    persistRecordingChunk(e.data).catch((err) => {
                        console.error("Failed to persist recording chunk", err);
                    });
                }
            };
            mediaRecorder.onstart = () => {
                setRecording(true);
                setStatusMessage("Recording…");
                if (!recordingStartedAtRef.current) {
                    setRecordingStartedAt(Date.now());
                }
                if (!patientId) setShowPatientDrawer(true);
            };
            mediaRecorder.onstop = () => {
                const blob = new Blob(chunks, { type: chunks[0]?.type || "audio/webm" });
                setRecordedBlob(blob);
                const url = URL.createObjectURL(blob);
                setAudioUrl(url);
                const f = new File([blob], `recording_${Date.now()}.webm`, { type: blob.type });
                setFile(f);
                setRecording(false);
                setStatusMessage("Recording ready.");
                setShowPatientDrawer(false);
                const latestRecordingId = activeRecordingIdRef.current;
                const latestChunkCount = (chunkIndexRef.current ?? -1) + 1;
                if (latestRecordingId) {
                    setSelectedRecordingId(latestRecordingId);
                }
                setPromptChunkCount(latestChunkCount);
                setShowPostRecordingPrompt(true);
                try { mediaRecorder.stream.getTracks().forEach(t => t.stop()); } catch (e) { }
            };
            mediaRecorder.start(60000);
        } catch (err) {
            console.error("Could not start recording:", err);
            setStatusMessage("Microphone access denied.");
        }
    };

    const persistRecordingChunk = async (blob) => {
        if (!auth.currentUser || !patientId) return;
        const userId = auth.currentUser.uid;
        const recordingId = activeRecordingIdRef.current || `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const nextChunkIndex = (chunkIndexRef.current ?? -1) + 1;
        activeRecordingIdRef.current = recordingId;
        chunkIndexRef.current = nextChunkIndex;
        const storagePath = `recordings/${userId}/${patientId}/${recordingId}/chunks/${nextChunkIndex}.webm`;
        const fileRef = storageRef(storage, storagePath);

        await uploadBytes(fileRef, blob, { contentType: blob.type || "audio/webm" });

        await setDoc(
            doc(db, "patients", patientId, "recordings", recordingId),
            {
                patientId,
                ownerId: userId,
                status: "pending_transcription",
                recordingStartAt: recordingStartedAtRef.current || Date.now(),
                recordingEndAt: Date.now(),
                updatedAt: serverTimestamp(),
                createdAt: serverTimestamp(),
                chunkCount: nextChunkIndex + 1,
                lastChunkIndex: nextChunkIndex,
                lastChunkAt: serverTimestamp(),
                storagePrefix: `recordings/${userId}/${patientId}/${recordingId}/chunks/`,
            },
            { merge: true }
        );

        await setDoc(
            doc(db, "patients", patientId, "recordings", recordingId, "chunks", `${nextChunkIndex}`),
            {
                index: nextChunkIndex,
                storagePath,
                createdAt: serverTimestamp(),
                mimeType: blob.type || "audio/webm",
                fileName: `chunk_${nextChunkIndex}.webm`,
            },
            { merge: true }
        );

        setActiveRecordingId(recordingId);
        setChunkIndex(nextChunkIndex);
    };

    const toMillis = (value) => {
        if (!value) return 0;
        if (typeof value === "number") return value;
        if (value.seconds) return value.seconds * 1000;
        if (value.toDate) return value.toDate().getTime();
        return 0;
    };

    const resumeRecording = (recording) => {
        setActiveRecordingId(recording.id);
        setChunkIndex(typeof recording.lastChunkIndex === "number" ? recording.lastChunkIndex : -1);
        setRecordingStartedAt(toMillis(recording.recordingStartAt) || Date.now());
        setStatusMessage("Ready to resume recording.");
    };

    const continueRecordingFromPrompt = async () => {
        setShowPostRecordingPrompt(false);
        setStatusMessage("Resuming recording…");
        await startRecording();
    };

    const handlePromptTranscribe = async () => {
        setShowPostRecordingPrompt(false);
        await handleTranscribe();
    };

    const selectRecordingForTranscription = (recording) => {
        setSelectedRecordingId(recording.id);
        setActiveRecordingId(recording.id);
        setChunkIndex(typeof recording.lastChunkIndex === "number" ? recording.lastChunkIndex : -1);
        setRecordingStartedAt(toMillis(recording.recordingStartAt) || Date.now());
        setStatusMessage("Selected pending recording for transcription.");
    };

    const stopRecording = () => {
        const mr = mediaRecorderRef.current;
        if (!mr || mr.state === "inactive") return;
        mr.stop();
    };

    const handleSendChat = async () => {
        if (!userInput.trim()) return;
        const newHistory = [...chatHistory, { role: "user", content: userInput }];
        setChatHistory(newHistory);
        setUserInput("");
        setChatLoading(true);
        try {
            const discussUrl = process.env.REACT_APP_DISCUSS_URL;
            if (!discussUrl) throw new Error("REACT_APP_DISCUSS_URL is not set");
            const idToken = await auth.currentUser?.getIdToken();
            if (!idToken) throw new Error("User not authenticated");
            const resp = await fetch(discussUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
                body: JSON.stringify({ transcript, userQuestion: userInput, chatHistory: newHistory }),
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data?.error || "Discuss request failed");
            setChatHistory((prev) => [...prev, { role: "assistant", content: data.reply }]);
        } catch (err) {
            console.error("Chat error:", err);
            alert(`Chat failed: ${err.message}`);
        } finally {
            setChatLoading(false);
        }
    };

    const handleChatKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendChat(); }
    };

    const hasAudio = !!(file || recordedBlob);
    const hasPatient = !!patientId;
    const hasRecording = recording || hasAudio;
    const hasNote = !!clinicalNote || !!processedNoteId || loading;
    const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
    const promptHasChunks = promptChunkCount > 0;
    const promptCanTranscribe = promptHasChunks && !!patientId && !loading;

    return (
        <div className="transcribe-layout">
            <aside className="hero-summary">
                <div className="hero-summary-title">Visit Journey</div>
                <div className={`hero-step ${hasPatient ? "active" : ""}`}>
                    <div className="hero-step-icon">👤</div>
                    <div>
                        <div className="hero-step-label">Patient selection</div>
                        <div className="hero-step-status">
                            {hasPatient ? (selectedPatientName || "Patient selected") : "No patient selected"}
                        </div>
                    </div>
                </div>
                <div className={`hero-step ${hasRecording ? "active" : ""}`}>
                    <div className="hero-step-icon">🎙</div>
                    <div>
                        <div className="hero-step-label">Record</div>
                        <div className="hero-step-status">
                            {hasRecording ? "Recording started" : "Recording not started"}
                        </div>
                    </div>
                </div>
                <div className={`hero-step ${hasNote ? "active" : ""}`}>
                    <div className="hero-step-icon">✏️</div>
                    <div>
                        <div className="hero-step-label">Transcribe + Generate</div>
                        <div className="hero-step-status">
                            {hasNote ? "Clinical note in progress" : "No Clinical Note"}
                        </div>
                    </div>
                </div>
            </aside>

            <div className="transcribe-shell">

                {/* ── 1. Record (hero) ── */}
                <div className={`record-hero ${transcriptionLimitError ? "record-hero-error" : ""}`}>
                    <div className="record-eyebrow">Step 01 — Record Visit Audio</div>

                    <div className="record-layout">
                        <button
                            className={`mic-btn ${recording ? "recording" : ""}`}
                            onClick={recording ? stopRecording : startRecording}
                            disabled={loading}
                            title={recording ? "Stop recording" : "Start recording"}
                        >
                            {recording ? "⏹" : "🎙"}
                        </button>

                        <div className="record-info">
                            <div className="record-status">
                                {recording ? "Recording in Progress" : hasAudio ? "Audio Ready" : "Ready to Record"}
                            </div>
                            <div className="record-sub">
                                {recording
                                    ? "Tap the button to stop"
                                    : hasAudio
                                        ? file?.name || "recording.webm"
                                        : "Tap the circle to begin capturing the visit"}
                            </div>
                            <div className={`waveform ${recording ? "active" : ""}`}>
                                {[...Array(10)].map((_, i) => (
                                    <div key={i} className="waveform-bar" />
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="record-select-row">
                        <div className="record-select-copy">
                            {patientId
                                ? "Patient linked. You can keep recording or change the patient."
                                : "Recording runs in the background. Select or create a patient now."}
                        </div>
                        <button
                            className="btn-outline-light"
                            onClick={() => setShowPatientDrawer(true)}
                            disabled={loading}
                        >
                            {patientId ? "Change patient" : "Select patient"}
                        </button>
                    </div>

                    <div className="record-footer">
                        <label style={{ cursor: "pointer" }}>
                            <input
                                type="file"
                                accept="audio/*"
                                onChange={handleFileChange}
                                style={{ display: "none" }}
                            />
                            <span className="btn-outline-light" style={{
                                display: "inline-flex", alignItems: "center", gap: "0.5rem",
                                fontFamily: "var(--font-body)", fontSize: "0.72rem", fontWeight: 600,
                                letterSpacing: "0.12em", textTransform: "uppercase",
                                padding: "0.65rem 1.4rem", borderRadius: "var(--radius-sm)",
                                cursor: "pointer",
                            }}>
                                📂 Upload File
                            </span>
                        </label>

                        {statusMessage && !loading && (
                            <span className="record-footer-status">{statusMessage}</span>
                        )}

                        <button
                            className="btn-cta"
                            onClick={handleTranscribe}
                            disabled={loading || !hasAudio}
                            style={{ marginLeft: "auto" }}
                        >
                            {loading ? (
                                <>
                                    <span style={{
                                        display: "inline-block", width: 12, height: 12,
                                        border: "2px solid rgba(255,255,255,0.3)",
                                        borderTopColor: "#fff", borderRadius: "50%",
                                        animation: "spin 0.7s linear infinite",
                                    }} />
                                    Processing…
                                </>
                            ) : "Transcribe & Generate"}
                        </button>
                    </div>

                    {loading && (
                        <div className="progress-section">
                            <div className="progress-label">
                                <span className="blink-dot" />
                                {statusMessage}
                            </div>
                            <div className="progress-track">
                                <div className="progress-fill" style={{ width: `${uploadProgress || 60}%` }} />
                            </div>
                        </div>
                    )}
                </div>

                {transcriptionLimitError && (
                    <div className="record-error">
                        {transcriptionLimitError}
                    </div>
                )}

                {showPatientDrawer && (
                    <div className="patient-drawer-overlay" onClick={() => setShowPatientDrawer(false)}>
                        <aside className="patient-drawer" onClick={(e) => e.stopPropagation()}>
                            <div className="patient-drawer-header">
                                <div>
                                    <div className="section-eyebrow">Recording</div>
                                    <h2 className="section-title" style={{ marginBottom: "0.25rem" }}>
                                        <span className="section-title-line" />
                                        Select Patient
                                    </h2>
                                    <p className="patient-drawer-sub">
                                        Recording continues while you select or create a patient.
                                    </p>
                                </div>
                                <button className="drawer-close" onClick={() => setShowPatientDrawer(false)}>
                                    ✕
                                </button>
                            </div>
                            <PatientManager
                                currentUser={currentUser}
                                onSelectPatient={(id) => setPatientId(id)}
                                selectedPatientId={patientId}
                            />
                        </aside>
                    </div>
                )}

                {showPostRecordingPrompt && (
                    <div className="post-recording-overlay">
                        <aside className="post-recording-panel">
                            <div className="post-recording-header">
                                <div>
                                    <div className="section-eyebrow">Recording paused</div>
                                    <h2 className="section-title" style={{ marginBottom: "0.35rem" }}>
                                        <span className="section-title-line" />
                                        Next step
                                    </h2>
                                    <p className="text-body" style={{ margin: 0 }}>
                                        Choose how you want to continue this visit.
                                    </p>
                                </div>
                                <button
                                    className="drawer-close"
                                    onClick={() => setShowPostRecordingPrompt(false)}
                                    aria-label="Close"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="post-recording-body">
                                <div className="post-recording-row">
                                    <div>
                                        <div className="post-recording-label">Continue recording</div>
                                        <div className="post-recording-sub">Keep adding to the same visit audio.</div>
                                    </div>
                                    <button
                                        className="btn-primary"
                                        type="button"
                                        onClick={continueRecordingFromPrompt}
                                        disabled={loading}
                                    >
                                        Continue recording
                                    </button>
                                </div>

                                <div className="post-recording-row">
                                    <div>
                                        <div className="post-recording-label">Select Clinical Note template</div>
                                        <div className="post-recording-sub">Choose the format for your note.</div>
                                    </div>
                                    <select
                                        className="post-recording-select"
                                        value={selectedTemplateId || "default"}
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            selectTemplate(value === "default" ? null : value);
                                        }}
                                    >
                                        <option value="default">Default SOAP</option>
                                        {templates.map((t) => (
                                            <option key={t.id} value={t.id}>
                                                {t.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="post-recording-row">
                                    <div>
                                        <div className="post-recording-label">Transcribe &amp; generate</div>
                                        <div className="post-recording-sub">
                                            {promptHasChunks
                                                ? `${promptChunkCount} chunks ready`
                                                : "No chunks recorded yet"}
                                        </div>
                                    </div>
                                    <button
                                        className="btn-cta"
                                        type="button"
                                        onClick={handlePromptTranscribe}
                                        disabled={!promptCanTranscribe}
                                    >
                                        Transcribe &amp; Generate
                                    </button>
                                </div>
                            </div>
                        </aside>
                    </div>
                )}

                {/* ── 2. Patients ── */}
                <div className="card card-tiffany">
                    <div className="section-eyebrow">Select Patient</div>
                    <h2 className="section-title">
                        <span className="section-title-line" />
                        Patients
                    </h2>
                    <PatientManager
                        currentUser={currentUser}
                        onSelectPatient={(id) => setPatientId(id)}
                        selectedPatientId={patientId}
                    />
                    {patientId && (
                        <div style={{ marginTop: "1rem" }}>
                            <div className="section-eyebrow">Pending recordings</div>
                            {pendingLoading && <div className="text-body">Loading pending recordings…</div>}
                            {!pendingLoading && pendingRecordings.length === 0 && (
                                <div className="text-body">No pending recordings.</div>
                            )}
                            {!pendingLoading && pendingRecordings.length > 0 && (
                                <div style={{ display: "grid", gap: "0.75rem" }}>
                                    {pendingRecordings.map((rec) => (
                                        <div
                                            key={rec.id}
                                            style={{
                                                border: "1px solid var(--border)",
                                                borderRadius: "var(--radius-md)",
                                                padding: "0.75rem 1rem",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: "1rem",
                                            }}
                                        >
                                            <div>
                                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                    <div style={{ fontWeight: 600, color: "var(--ink)" }}>
                                                        {new Date(toMillis(rec.recordingStartAt)).toLocaleString()}
                                                    </div>
                                                    {rec.id === activeRecordingId && (
                                                        <span className="session-badge">Active session</span>
                                                    )}
                                                    {rec.id === selectedRecordingId && (
                                                        <span className="session-badge">Selected</span>
                                                    )}
                                                </div>
                                                <div style={{ fontSize: "0.8rem", color: "var(--ink-faint)" }}>
                                                    {rec.chunkCount || 0} chunks
                                                </div>
                                            </div>
                                            <div style={{ display: "flex", gap: "0.5rem" }}>
                                                <button
                                                    className={rec.id === activeRecordingId ? "btn-outline" : "btn-warning"}
                                                    type="button"
                                                    onClick={() => resumeRecording(rec)}
                                                >
                                                    {rec.id === activeRecordingId ? "Continue recording above" : "Resume recording"}
                                                </button>
                                                <button
                                                    className="btn-primary"
                                                    type="button"
                                                    onClick={() => selectRecordingForTranscription(rec)}
                                                >
                                                    Transcribe
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {audioUrl && (
                    <div className="card">
                        <div className="section-eyebrow">Audio Preview</div>
                        <h3 className="section-title">
                            <span className="section-title-line" />
                            Recorded Audio
                        </h3>
                        <div className="audio-wrap">
                            <audio controls src={audioUrl} />
                        </div>
                    </div>
                )}

                <div className="card">
                    <div className="section-eyebrow">Step 02 — Choose Format</div>
                    <h3 className="section-title">
                        <span className="section-title-line" />
                        Note Templates
                    </h3>

                    <div className="template-pills">
                        <span
                            className={`pill ${!selectedTemplateId ? "active" : ""}`}
                            onClick={() => selectTemplate(null)}
                        >
                            Default SOAP
                        </span>
                        {templates.map(t => (
                            <span
                                key={t.id}
                                className={`pill ${selectedTemplateId === t.id ? "active" : ""}`}
                                onClick={() => selectTemplate(t.id)}
                            >
                                {t.name}
                                <button
                                    className="pill-delete"
                                    onClick={(e) => { e.stopPropagation(); deleteTemplate(t.id); }}
                                    title={`Delete ${t.name}`}
                                >
                                    ✕
                                </button>
                            </span>
                        ))}
                    </div>

                    <details>
                        <summary>+ Create new template</summary>
                        <div style={{ display: "grid", gap: "0.85rem" }}>
                            <div className="form-row">
                                <div className="form-field">
                                    <label className="form-label">Template name</label>
                                    <input
                                        placeholder="e.g. Root Canal Consult"
                                        value={templateName}
                                        onChange={(e) => setTemplateName(e.target.value)}
                                    />
                                </div>
                                <button
                                    className="btn-tiffany"
                                    onClick={saveTemplate}
                                    style={{ alignSelf: "flex-end" }}
                                >
                                    Save
                                </button>
                            </div>
                            <div className="form-field">
                                <label className="form-label">Template content</label>
                                <textarea
                                    placeholder="Paste your template. Use {{placeholders}} as needed."
                                    value={templateText}
                                    onChange={(e) => setTemplateText(e.target.value)}
                                    rows={5}
                                />
                            </div>
                        </div>
                    </details>

                    {selectedTemplate && (
                        <div style={{
                            marginTop: "0.85rem", padding: "0.75rem 1rem",
                            background: "var(--tiffany-pale)", borderRadius: "var(--radius-sm)",
                            fontSize: "0.82rem", color: "var(--ink-mid)",
                            border: "1px solid #C8E8E6",
                        }}>
                            <strong style={{ color: "var(--tiffany-dark)", fontWeight: 600 }}>Active:</strong>{" "}
                            {selectedTemplate.name}
                        </div>
                    )}
                </div>

                {patientId && (
                    <PatientNotes
                        patientId={patientId}
                        onSelectNote={(note) => {
                            setTranscript(note.transcription || "");
                            setClinicalNote(note.clinicalNotes || "");
                            setProcessedNoteId(note.id || null);
                            setStatusMessage(`Loaded note ${note.id}`);
                        }}
                    />
                )}

                {patientId && (
                    <PatientClinicalNotes
                        patientId={patientId}
                        onSelectClinicalNote={(note) => {
                            setClinicalNote(note.clinicalNotes || "");
                            if (note.transcription) setTranscript(note.transcription);
                            setProcessedNoteId(note.id || null);
                            setStatusMessage(`Loaded note ${note.id}`);
                        }}
                    />
                )}

                {/* {transcript && (
                <div className="card">
                    <div className="section-eyebrow">Output — Raw Transcript</div>
                    <h3 className="section-title">
                        <span className="section-title-line" />
                        Transcript
                    </h3>
                    <div className="text-body">{transcript}</div>
                </div>
            )} */}

                <div className="card card-tiffany">
                    <div className="section-eyebrow">Step 3: Generated Note</div>
                    <h3 className="section-title">
                        <span className="section-title-line" />
                        Clinical Note
                    </h3>
                    {clinicalNote ? (
                        <div className="note-body">{clinicalNote}</div>
                    ) : (
                        <div className="note-empty">
                            No note generated yet.<br />
                            <strong>Record a visit and tap Transcribe &amp; Generate.</strong>
                        </div>
                    )}
                </div>

                {transcript && (
                    <div className="card">
                        <div className="section-eyebrow">Step 03 — Clarify &amp; Discuss</div>
                        <h3 className="section-title">
                            <span className="section-title-line" />
                            Discuss this Visit
                        </h3>
                        <div className="chat-window">
                            {chatHistory.length === 0 ? (
                                <p className="chat-empty">Ask anything about this visit…</p>
                            ) : (
                                chatHistory.map((msg, i) => (
                                    <div key={i} className={`chat-bubble ${msg.role}`}>
                                        {msg.content}
                                    </div>
                                ))
                            )}
                            <div ref={chatEndRef} />
                        </div>
                        <div className="chat-input-row">
                            <input
                                type="text"
                                value={userInput}
                                onChange={(e) => setUserInput(e.target.value)}
                                onKeyDown={handleChatKeyDown}
                                placeholder="Ask about medications, procedures, follow-ups…"
                            />
                            <button
                                className="btn-primary"
                                onClick={handleSendChat}
                                disabled={chatLoading || !userInput.trim()}
                            >
                                {chatLoading ? (
                                    <span style={{
                                        display: "inline-block", width: 12, height: 12,
                                        border: "2px solid rgba(255,255,255,0.3)",
                                        borderTopColor: "#fff", borderRadius: "50%",
                                        animation: "spin 0.7s linear infinite",
                                    }} />
                                ) : "Send"}
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}

export default AudioUpload;
